import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const inFlightRef = useRef(false);

  const loadSettings = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const timestamp = Date.now();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/settings?_t=${timestamp}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadSettings();

    // Refresh quando a aba ficar visível novamente
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadSettings();
      }
    };

    // Refresh quando a janela ganhar foco
    const handleFocus = () => loadSettings();

    // Refresh quando o admin disparar atualização (mesma aba)
    const handleSettingsUpdated = () => loadSettings();

    // Refresh quando outra aba atualizar (localStorage sync)
    const handleStorage = (e) => {
      if (e.key === 'kitstop:settings:updated') loadSettings();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('settings:updated', handleSettingsUpdated);
    window.addEventListener('storage', handleStorage);

    // Polling leve a cada 20s como fallback
    const interval = setInterval(loadSettings, 20000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('settings:updated', handleSettingsUpdated);
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [loadSettings]);

  const company = settings?.company || {
    company_name: '',
    app_name: '',
    logo_url: '',
    whatsapp: '',
    email: '',
    phone: '',
    address: '',
    footer_text: '',
    instagram: '',
    facebook: '',
  };

  const payment = settings?.payment || {
    pix_enabled: true,
    pix_key: '',
    pix_key_type: 'cpf',
    pix_receiver_name: '',
    pix_bank: '',
    credit_enabled: true,
    credit_brands: ['visa', 'mastercard', 'elo'],
    credit_installments_max: 12,
    credit_installments_min_value: 50,
    debit_enabled: true,
    debit_brands: ['visa', 'mastercard', 'elo'],
  };

  const getPaymentMethodsText = useCallback(() => {
    const methods = [];
    if (payment.pix_enabled) methods.push('Pix');
    if (payment.debit_enabled) methods.push('Débito');
    if (payment.credit_enabled) {
      methods.push(`Crédito até ${payment.credit_installments_max}x`);
    }
    return methods.join(' • ');
  }, [payment]);

  const getEnabledPaymentMethods = useCallback(() => {
    const methods = [];
    if (payment.pix_enabled) methods.push('pix');
    if (payment.debit_enabled) methods.push('debit_card');
    if (payment.credit_enabled) methods.push('credit_card');
    return methods;
  }, [payment]);

  const value = {
    settings,
    loading,
    refreshSettings: loadSettings,
    getPaymentMethodsText,
    getEnabledPaymentMethods,
    company,
    payment,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
